import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, TextField, Typography,
} from '@mui/material';
import { PRES, TYPE } from '../chrome';
import { COPY, pageFormatLabel } from '../presentationCopy';
import { PresentationLanguage, PresentationMarket, PresentationTemplate, VisibleAsset } from '../types';
import {
  cancelGeneration,
  clarifyGeneration,
  GenerationRun,
  pollGeneration,
  resumeGeneration,
  searchGenerationProducts,
  startGeneration,
  uploadGenerationSource,
} from './api';

interface GenerateDialogProps {
  open: boolean;
  templates: PresentationTemplate[];
  language: PresentationLanguage;
  market: PresentationMarket;
  deckCategoryId?: string | null;
  onClose: () => void;
  onCreated: (presentationId: string) => void;
}

function isValidArticle(asset: Pick<VisibleAsset, 'articleNumber'>): boolean {
  return /^[A-Z0-9][A-Z0-9._/-]{1,31}$/i.test(String(asset.articleNumber || '').trim());
}

function newIdempotencyKey(): string {
  const webCrypto = typeof globalThis !== 'undefined' ? (globalThis as { crypto?: { randomUUID?: () => string } }).crypto : undefined;
  if (webCrypto?.randomUUID) return webCrypto.randomUUID();
  return `gen-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

const GenerateDialog: React.FC<GenerateDialogProps> = ({
  open, templates, language, market, deckCategoryId, onClose, onCreated,
}) => {
  const [prompt, setPrompt] = useState('');
  const [templateId, setTemplateId] = useState(templates[0]?._id || '');
  const [productQuery, setProductQuery] = useState('');
  const [products, setProducts] = useState<VisibleAsset[]>([]);
  const [selected, setSelected] = useState<VisibleAsset[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [run, setRun] = useState<GenerationRun | null>(null);
  const [clarify, setClarify] = useState('');
  const [workbookSourceId, setWorkbookSourceId] = useState('');
  const [workbookSheet, setWorkbookSheet] = useState('');

  const runIdRef = useRef<string | null>(null);
  const pollAbortRef = useRef<AbortController | null>(null);
  const submittingRef = useRef(false);
  const abortedRef = useRef(false);
  const idempotencyKeyRef = useRef('');

  useEffect(() => {
    if (!open) return;
    setPrompt('');
    setSelected([]);
    setFiles([]);
    setError(null);
    setRun(null);
    setClarify('');
    setWorkbookSourceId('');
    setWorkbookSheet('');
    setTemplateId(templates[0]?._id || '');
    setBusy(false);
    submittingRef.current = false;
    abortedRef.current = false;
    runIdRef.current = null;
    pollAbortRef.current?.abort();
    pollAbortRef.current = null;
    idempotencyKeyRef.current = newIdempotencyKey();
  }, [open, templates]);

  useEffect(() => {
    if (!productQuery.trim()) {
      setProducts([]);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const page = await searchGenerationProducts(productQuery, { limit: 8 }, controller.signal);
        setProducts((page.items || []).filter(isValidArticle) as VisibleAsset[]);
      } catch {
        if (!controller.signal.aborted) setProducts([]);
      }
    }, 250);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [productQuery]);

  const workbookSources = (run?.sources || []).filter((source) => source.kind === 'workbook' && source.sheets?.length);
  const activeWorkbook = workbookSources.find((source) => source.id === workbookSourceId) || workbookSources[0];

  useEffect(() => {
    if (!activeWorkbook) return;
    setWorkbookSourceId(activeWorkbook.id);
    setWorkbookSheet((current) => current || activeWorkbook.sheets?.[0]?.name || '');
  }, [activeWorkbook]);

  const watchRun = async (next: GenerationRun) => {
    runIdRef.current = next._id;
    setRun(next);
    if (abortedRef.current) return;
    pollAbortRef.current?.abort();
    const controller = new AbortController();
    pollAbortRef.current = controller;
    try {
      const finished = await pollGeneration(next._id, setRun, controller.signal);
      if (abortedRef.current) return;
      setRun(finished);
      if (finished.status === 'completed' && finished.presentationId) {
        onCreated(String(finished.presentationId));
      } else if (finished.status === 'failed') {
        setError(finished.errorMessage || COPY.createWithAiFailed);
      } else if (finished.status === 'cancelled') {
        setError(COPY.createWithAiCancelled);
      }
    } catch (err: any) {
      if (abortedRef.current || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
      const message = err?.response?.data?.message || err?.message || COPY.createWithAiFailed;
      setError(/tid|timeout/i.test(String(message)) ? COPY.createWithAiTimeout : message);
    }
  };

  const submit = async () => {
    if (submittingRef.current || busy || !prompt.trim()) return;
    submittingRef.current = true;
    abortedRef.current = false;
    setBusy(true);
    setError(null);
    try {
      const created = await startGeneration({
        prompt: prompt.trim(),
        language,
        market,
        templateId: templateId || undefined,
        assetIds: selected.map((item) => item._id),
        articleNumbers: selected.map((item) => item.articleNumber).filter(Boolean) as string[],
        deckCategoryId: deckCategoryId || null,
        autoStart: files.length === 0,
      }, idempotencyKeyRef.current);
      runIdRef.current = created._id;
      setRun(created);
      if (abortedRef.current) return;
      for (const file of files) {
        if (abortedRef.current) return;
        await uploadGenerationSource(created._id, file);
      }
      if (abortedRef.current) return;
      const started = files.length ? await resumeGeneration(created._id) : created;
      await watchRun(started);
    } catch (err: any) {
      if (!abortedRef.current) setError(err?.response?.data?.message || COPY.createWithAiFailed);
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  const continueClarify = async () => {
    if (!run || submittingRef.current) return;
    submittingRef.current = true;
    abortedRef.current = false;
    setBusy(true);
    setError(null);
    try {
      for (const file of files) {
        await uploadGenerationSource(run._id, file);
      }
      const workbookRanges = run.clarification?.code === 'workbook-range' && activeWorkbook && workbookSheet
        ? [{ sourceId: activeWorkbook.id, sheet: workbookSheet }]
        : undefined;
      const next = await clarifyGeneration(run._id, {
        clarifyAnswer: clarify,
        assetIds: selected.map((item) => item._id),
        articleNumbers: selected.map((item) => item.articleNumber).filter(Boolean) as string[],
        workbookRanges,
      });
      await watchRun(next);
    } catch (err: any) {
      if (!abortedRef.current) setError(err?.response?.data?.message || COPY.createWithAiFailed);
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  const handleClose = async () => {
    abortedRef.current = true;
    pollAbortRef.current?.abort();
    const runId = runIdRef.current;
    if (runId && busy) {
      try {
        await cancelGeneration(runId);
      } catch {
        // Local abort still closes the dialog.
      }
    }
    setBusy(false);
    submittingRef.current = false;
    onClose();
  };

  return (
    <Dialog open={open} onClose={() => { if (!busy) void handleClose(); }} fullWidth maxWidth="sm">
      <DialogTitle>{COPY.createWithAiTitle}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
        <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.body, color: PRES.textSecondary }}>{COPY.createWithAiHelp}</Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {run?.status === 'needs_clarification' && (
          <Alert severity="info">{run.clarification?.message || COPY.createWithAiClarify}</Alert>
        )}
        {busy && run && run.status !== 'needs_clarification' && (
          <Alert severity="info">{COPY.createWithAiWorking}</Alert>
        )}
        <TextField
          autoFocus
          multiline
          minRows={4}
          label={COPY.createWithAiPrompt}
          placeholder={COPY.createWithAiPromptPlaceholder}
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          disabled={busy}
        />
        {templates.length > 0 && (
          <Select size="small" value={templateId} onChange={(event) => setTemplateId(event.target.value)} disabled={busy}>
            {templates.map((template) => (
              <MenuItem key={template._id} value={template._id}>
                {template.name} · {pageFormatLabel(template.pageFormat)}
              </MenuItem>
            ))}
          </Select>
        )}
        <TextField
          size="small"
          label={COPY.createWithAiProductSearch}
          value={productQuery}
          onChange={(event) => setProductQuery(event.target.value)}
          disabled={busy}
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {selected.map((item) => (
            <Chip
              key={item._id}
              label={`${item.articleNumber || ''} ${item.title}`.trim()}
              onDelete={busy ? undefined : () => setSelected((current) => current.filter((row) => row._id !== item._id))}
            />
          ))}
          {products.filter((item) => !selected.some((row) => row._id === item._id)).map((item) => (
            <Chip
              key={item._id}
              variant="outlined"
              disabled={busy}
              label={`${item.articleNumber || ''} ${item.title}`.trim()}
              onClick={busy ? undefined : () => setSelected((current) => [...current, item])}
            />
          ))}
        </Box>
        <Button component="label" disabled={busy && run?.status !== 'needs_clarification'} sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
          {COPY.createWithAiAttach}
          <input
            hidden
            multiple
            type="file"
            accept=".pdf,.docx,.pptx,.txt,.html,.xlsx,image/png,image/jpeg,image/webp"
            onChange={(event) => setFiles(Array.from(event.target.files || []))}
          />
        </Button>
        {files.length > 0 && (
          <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.caption }}>{files.map((file) => file.name).join(', ')}</Typography>
        )}
        {run?.status === 'needs_clarification' && run.clarification?.code === 'workbook-range' && workbookSources.length > 0 && (
          <>
            {workbookSources.length > 1 && (
              <Select
                size="small"
                value={activeWorkbook?.id || ''}
                onChange={(event) => {
                  setWorkbookSourceId(event.target.value);
                  const next = workbookSources.find((source) => source.id === event.target.value);
                  setWorkbookSheet(next?.sheets?.[0]?.name || '');
                }}
                disabled={busy}
              >
                {workbookSources.map((source) => (
                  <MenuItem key={source.id} value={source.id}>{source.fileName}</MenuItem>
                ))}
              </Select>
            )}
            <Select
              size="small"
              value={workbookSheet}
              onChange={(event) => setWorkbookSheet(event.target.value)}
              disabled={busy}
            >
              {(activeWorkbook?.sheets || []).map((sheet) => (
                <MenuItem key={sheet.name} value={sheet.name}>
                  {sheet.name} ({sheet.rows}×{sheet.cols})
                </MenuItem>
              ))}
            </Select>
          </>
        )}
        {run?.status === 'needs_clarification' && run.clarification?.code !== 'workbook-range' && (
          <TextField
            label={COPY.createWithAiClarify}
            value={clarify}
            onChange={(event) => setClarify(event.target.value)}
            disabled={busy}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { void handleClose(); }} sx={{ textTransform: 'none' }}>
          {COPY.cancel}
        </Button>
        {run?.status === 'needs_clarification' ? (
          <Button variant="contained" disabled={busy} onClick={() => { void continueClarify(); }} sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none' }}>
            {COPY.createWithAiContinue}
          </Button>
        ) : (
          <Button variant="contained" disabled={busy || !prompt.trim()} onClick={() => { void submit(); }} sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none' }}>
            {COPY.createWithAiStart}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GenerateDialog;
